/**
 * Locations Store (Zustand)
 *
 * Manages store location data with Firestore as source of truth.
 * Initialized with bundled data for immediate availability,
 * then synced with Firestore for live updates.
 *
 * This replaces the static LOCATIONS constant with a reactive store
 * that updates in real-time when locations are modified in the admin panel.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Import bundled data as initial fallback
import locationsData from '../config/locations.json';

/**
 * Location with full display name and optional address
 */
export interface Location {
  id: string;
  name: string;
  address?: string;
  timezone?: string;
}

/**
 * Raw location data from Firestore/JSON (short names only)
 */
interface RawLocation {
  id: string;
  name: string;
  address?: string;
  timezone?: string;
}

/**
 * Default timezone used when not specified
 */
const DEFAULT_TIMEZONE = 'America/Denver';

/**
 * Transform raw location data to full display format
 */
const transformLocations = (rawLocations: RawLocation[]): Location[] =>
  rawLocations.map((loc) => ({
    id: loc.id,
    name: `${loc.id} - ${loc.name}`,
    address: loc.address,
    timezone: loc.timezone,
  }));

/**
 * Initial locations from bundled JSON (fallback)
 */
const initialLocations = transformLocations(locationsData.locations as RawLocation[]);
const initialDefaultLocationId = locationsData.defaultLocationId;

interface LocationsState {
  // Data
  locations: Location[];
  rawLocations: RawLocation[];
  defaultLocationId: string;

  // Loading state
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  setLocations: (rawLocations: RawLocation[], defaultLocationId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInitialized: (initialized: boolean) => void;

  // Helper methods (computed from state)
  getLocationName: (locationId: string) => string;
  getLocationShortName: (locationId: string) => string;
  getLocationAddress: (locationId: string) => string | undefined;
  getLocationTimezone: (locationId: string) => string;
  isValidLocation: (locationId: string) => boolean;
  getLocationIds: () => string[];
  getLocationCount: () => number;
}

export const useLocationsStore = create<LocationsState>()(
  devtools(
    (set, get) => ({
      // Initial state from bundled data
      locations: initialLocations,
      rawLocations: locationsData.locations as RawLocation[],
      defaultLocationId: initialDefaultLocationId,
      isLoading: false,
      isInitialized: false,
      error: null,

      // Actions
      setLocations: (rawLocations, defaultLocationId) =>
        set({
          rawLocations,
          locations: transformLocations(rawLocations),
          defaultLocationId,
          error: null,
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      setInitialized: (initialized) => set({ isInitialized: initialized }),

      // Helper methods
      getLocationName: (locationId) => {
        const location = get().locations.find((loc) => loc.id === locationId);
        return location?.name || 'Unknown Location';
      },

      getLocationShortName: (locationId) => {
        const rawLoc = get().rawLocations.find((l) => l.id === locationId);
        return rawLoc?.name || 'Unknown';
      },

      getLocationAddress: (locationId) => {
        const rawLoc = get().rawLocations.find((l) => l.id === locationId);
        return rawLoc?.address || undefined;
      },

      getLocationTimezone: (locationId) => {
        const rawLoc = get().rawLocations.find((l) => l.id === locationId);
        return rawLoc?.timezone || DEFAULT_TIMEZONE;
      },

      isValidLocation: (locationId) => {
        return get().locations.some((loc) => loc.id === locationId);
      },

      getLocationIds: () => {
        return get().locations.map((loc) => loc.id);
      },

      getLocationCount: () => {
        return get().locations.length;
      },
    }),
    { name: 'locations-store' }
  )
);

/**
 * Selector hooks for common use cases
 */
export const useLocations = () => useLocationsStore((state) => state.locations);
export const useRawLocations = () => useLocationsStore((state) => state.rawLocations);
export const useDefaultLocationId = () => useLocationsStore((state) => state.defaultLocationId);
export const useLocationsLoading = () => useLocationsStore((state) => state.isLoading);

export const getRawLocations = () => useLocationsStore.getState().rawLocations;
export const getLocationName = (locationId: string) =>
  useLocationsStore.getState().getLocationName(locationId);
export const getLocationShortName = (locationId: string) =>
  useLocationsStore.getState().getLocationShortName(locationId);
export const getLocationAddress = (locationId: string) =>
  useLocationsStore.getState().getLocationAddress(locationId);
export const getLocationIds = () => useLocationsStore.getState().getLocationIds();

/**
 * @deprecated FROZEN AT BUILD TIME — do not use in new code.
 * These are snapshots of the bundled locations.json and will NOT reflect
 * locations added via the admin panel after the last build.
 * Use the useLocations() hook instead.
 */
export const LOCATIONS = initialLocations;
/** @deprecated See LOCATIONS — frozen at build time. Use useDefaultLocationId() instead. */
export const DEFAULT_LOCATION_ID = initialDefaultLocationId;
